import {
  Handedness,
  NormalizedTabletPoint,
  PenReport,
  SignatureTabletProfile,
  mapTabletToUnitSquare,
  normalizePressure,
  parseXpPenReport,
  selectTabletProfile,
  withReportId,
} from './g430s'

export interface TabletHidDevice {
  vendorId: number
  productId: number
  productName: string
  opened: boolean
  open(): Promise<void>
  close(): Promise<void>
  sendReport(reportId: number, data: BufferSource): Promise<void>
  addEventListener(
    type: 'inputreport',
    listener: (event: TabletInputReportEvent) => void,
  ): void
  removeEventListener(
    type: 'inputreport',
    listener: (event: TabletInputReportEvent) => void,
  ): void
}

export interface TabletInputReportEvent {
  reportId: number
  data: DataView
  device: TabletHidDevice
}

export interface TabletSessionListener {
  onConnection(
    profile: SignatureTabletProfile,
    reportLength: number | null,
  ): void
  onPoint(point: NormalizedTabletPoint): void
  onError(message: string): void
  onClose(): void
}

export async function sendTabletInit(
  device: {sendReport(reportId: number, data: BufferSource): Promise<void>},
  profile: SignatureTabletProfile,
): Promise<boolean> {
  const reportId = profile.initReport[0]
  const command = profile.initReport.slice(1)
  const sizes = [command.length, profile.outputReportLength - 1, 7, 11, 13]
  const unique = Array.from(
    new Set(sizes.filter(size => size >= command.length && size > 0)),
  )
  for (const size of unique) {
    const data = new Uint8Array(size)
    data.set(command)
    try {
      await device.sendReport(reportId, data)
      return true
    } catch (_error) {
      // The descriptor length differs between the 12-byte and 14-byte
      // firmwares. Try the next size before giving up.
    }
  }
  return false
}

/**
 * Reads absolute pen reports from a G430S and maps the active area onto the
 * signature frame. Pointer events are ignored by the canvas while this
 * session is open, so the official driver and this capture do not double-draw.
 */
export class SignatureTabletSession {
  private device: TabletHidDevice | null = null
  private profile: SignatureTabletProfile | null = null
  private identity: {
    vendorId: number
    productId: number
    productName: string
  } | null = null
  private reportLength: number | null = null
  private drawing = false
  private handedness: Handedness = 'right'
  private readonly onReport: (event: TabletInputReportEvent) => void

  constructor(
    private readonly listener: TabletSessionListener,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.onReport = event => {
      const payload = new Uint8Array(
        event.data.buffer,
        event.data.byteOffset,
        event.data.byteLength,
      )
      this.ingest(event.reportId, payload)
    }
  }

  get connected(): boolean {
    return this.device != null && this.profile != null
  }

  get activeProfile(): SignatureTabletProfile | null {
    return this.profile
  }

  setHandedness(value: Handedness) {
    this.handedness = value
  }

  identify(device: {
    vendorId: number
    productId: number
    productName?: string
  }): SignatureTabletProfile | null {
    const productName = device.productName || ''
    const profile = selectTabletProfile(
      device.vendorId,
      device.productId,
      undefined,
      productName,
    )
    if (!profile) {
      this.profile = null
      this.identity = null
      return null
    }
    this.profile = profile
    this.identity = {
      vendorId: device.vendorId,
      productId: device.productId,
      productName,
    }
    return profile
  }

  async attach(
    device: TabletHidDevice,
  ): Promise<SignatureTabletProfile | null> {
    const profile = this.identify(device)
    if (!profile) return null
    if (!device.opened) await device.open()
    this.device = device
    device.addEventListener('inputreport', this.onReport)
    await sendTabletInit(device, profile)
    this.listener.onConnection(profile, this.reportLength)
    return profile
  }

  async close() {
    const device = this.device
    this.finishStroke()
    this.device = null
    this.profile = null
    this.identity = null
    this.reportLength = null
    if (device) {
      device.removeEventListener('inputreport', this.onReport)
      try {
        if (device.opened) await device.close()
      } catch (error) {
        // Unplugging the tablet can reject close(). The session is already dropped.
      }
    }
    this.listener.onClose()
  }

  ingest(reportId: number, payload: Uint8Array) {
    if (!this.profile || !this.identity) return
    const report = withReportId(reportId, payload)
    const parsed = parseXpPenReport(report)
    if (!parsed) return

    if (this.reportLength !== report.length) {
      this.reportLength = report.length
      const refined = selectTabletProfile(
        this.identity.vendorId,
        this.identity.productId,
        report.length,
        this.identity.productName,
      )
      if (refined && refined.id !== this.profile.id) {
        this.profile = refined
        this.listener.onConnection(refined, report.length)
      }
    }

    this.applyPenReport(parsed)
  }

  private applyPenReport(parsed: PenReport) {
    if (!this.profile) return
    if (!parsed.inRange || !parsed.tipDown) {
      this.finishStroke()
      return
    }
    const mapped = mapTabletToUnitSquare(
      parsed.x,
      parsed.y,
      this.profile,
      this.handedness,
    )
    const point: NormalizedTabletPoint = {
      phase: this.drawing ? 'move' : 'down',
      x: mapped.x,
      y: mapped.y,
      pressure: normalizePressure(parsed.pressure, this.profile.maxPressure),
      time: this.now(),
    }
    this.drawing = true
    this.listener.onPoint(point)
  }

  private finishStroke() {
    if (!this.drawing) return
    this.drawing = false
    this.listener.onPoint({
      phase: 'up',
      x: 0,
      y: 0,
      pressure: 0,
      time: this.now(),
    })
  }
}
